import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './index.css';
import App from './App';
import posthog from 'posthog-js';
import { PostHogProvider, PostHogErrorBoundary } from '@posthog/react';
import { installDiagnostics } from './services/diagnostics';

const POSTHOG_KEY = process.env.REACT_APP_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_PUBLIC_POSTHOG_HOST;

if (POSTHOG_KEY && POSTHOG_HOST) {
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    defaults: '2026-01-30',

    // ── 2026-08-13: AZ AUTOCAPTURE MASZKOLÁSA — ⚠⚠ EZ KÉT KÜLÖN CONFIG-TENGELY!
    //
    // A 2026-08-02-i kör csak a SESSION REPLAY-t szigorította (lentebb), és a
    // kommentár azt sugallta, hogy ezzel a táblázatos pénzügyi adat kérdése
    // rendezve van. NEM volt: a `maskTextSelector` KIZÁRÓLAG a replayre hat, az
    // `$autocapture`-re semmilyen hatása nincs. Mérve a telepített
    // posthog-js@1.407.3 bundle-jéből:
    //
    //   • `shouldCaptureDomEvent`: minden `cursor:pointer`-es elem kattintása
    //     eseményt küld — és a CSS `cursor` ÖRÖKLŐDIK, tehát egy pointer-kurzoros
    //     `<tr>` minden `<td>`-je/`<span>`-je is triggerel;
    //   • a kattintott elem SAJÁT SZÖVEGE mindig kimegy (`$el_text`, 255 kar.);
    //   • nem-input elemnél MINDEN attribútum kimegy (`attr__*`, 1024 kar.),
    //     `<a>`-nál a `href` is (2048);
    //   • a szűrő (`ws`/`qs`) CSAK bankkártya- és SSN-mintát dob el.
    //
    // Ami ezen tényleg kimenne (2026-08-13-i állapot, ellenőrzött helyek):
    // partnernév + IBAN (`misc.js` tranzakciós log `title`-je), számlaszám és
    // összeg (`Documents.js` bizonylat-lista), a OneDrive-URL a PDF eredeti
    // fájlnevével. Ez pontosan az a halmaz, amit a
    // `.claude/rules/posthog-analitika.md` SOHA-ként tilt.
    //
    // ⚠ A hiba-irány itt nem szimmetrikus: egy hiányzó kattintás-statisztika
    // kellemetlen, egy külső feldolgozóhoz kiment IBAN visszavonhatatlan. Ezért
    // a TARTALMAT maszkoljuk, a SZERKEZETET nem: a `tag_name`/`classes`/`ids` és
    // az `$elements` lánc megmarad (azok külön mezők, nem `attr__*`), tehát az
    // „melyik gombra kattintottak" jel megmarad — a „mi volt ráírva" nem.
    //
    // A valódi termék-analitika amúgy sem ezen áll, hanem a 17 KURÁLT eseményen
    // (`capture`/`capture_for`), amiknek a property-i szándékosan csak számlálók
    // és flag-ek. Regresszió: `backend/tests/test_posthog_privacy.py`.
    mask_all_text: true,
    mask_all_element_attributes: true,

    // ── 2026-08-02 (admin F4): session replay maszkolás ──────────────────────
    // A `defaults: '2026-01-30'` bundle bekapcsolja a session replayt. A
    // posthog-js alapból az INPUT mezőket maszkolja, a sima szövegeket NEM —
    // vagyis a bizonylat-táblákban az összegek, IBAN-ok és partnernevek
    // felvételre kerültek volna. Fintech-adatnál ez nem vállalható, főleg mert
    // a PostHog-nak ma NINCS szerepkör-alapú hozzáférés-védelme a replay
    // megtekintéséhez (PostHog issue #44154).
    //
    // A `maskTextSelector` a maszkolandó elemeket jelöli ki. Minden táblát és
    // az explicit `.ph-mask` osztályt maszkoljuk — a navigáció, a gombok és a
    // címsorok láthatók maradnak, tehát a felvétel továbbra is használható
    // hibakeresésre.
    //
    // ⚠ 2026-09-24 (Codex A16): a `table` szelektor a fizetési modalt és a slide-over
    // részletpanelt NEM fogta — ott látszott a felvételen összeg, IBAN és partnernév.
    // Mostantól MINDEN dialógus (`[role="dialog"]` — a `UI.js` Modal és SlideOver is
    // ezt a szerepet adja) szövege maszkolt. A dashboard KPI-kártyái továbbra sem
    // (NYITOTT, a replay használhatóságát érinti — `wiki/posthog-analitika.md`).
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: 'table, .ph-mask, [data-ph-mask], [role="dialog"]',
    },

    // Kivétel-autocapture: eddig CSAK a PostHogErrorBoundary fogott el valamit
    // (vagyis a React render-hibákat), a fetch/async hibák nem látszottak.
    capture_exceptions: true,

    // ── 2026-09-16: OTel log-csatorna a böngészőből ──────────────────────────
    //
    // A `diagnostics.js` 20 elemű körkörös puffere eddig CSAK akkor jutott ki,
    // ha a felhasználó KÉZZEL hibát jelentett. Egy hiba, amit senki nem jelent,
    // nem létezett. A `service.name` a backend/worker elnevezéséhez illeszkedik
    // (`phenofin-backend` / `phenofin-worker`), hogy a napló egy nézetben
    // szűrhető legyen.
    //
    // ⚠⚠ A `captureConsoleLogs` SZÁNDÉKOSAN NINCS BEKAPCSOLVA, és ne is kapcsold be.
    // Az a `console`-t írná felül globálisan, és MINDEN konzolhívás argumentumát
    // kivinné. Mérve: a `pages/misc.js:109` `console.error('Doc upload after
    // employee create failed:', e)` második argumentuma egy axios-hiba, aminek a
    // `message`-e válasz-TÖRZSET hordozhat — abban pedig számla-adat van. A
    // PostHog saját doksija is így fogalmaz: „Overriding a system library like
    // `console` has a large blast radius." Ugyanaz az elv, mint a backend
    // log-csatornáján: ALLOWLIST, nem denylist — explicit `posthog.logger.*`
    // hívások, konstans üzenettel és ellenőrzött attribútumokkal.
    logs: {
      serviceName: 'phenofin-frontend',
      environment: process.env.NODE_ENV === 'production' ? 'production' : 'development',
    },
  });

  // ── Tenant-varrat LEZÁRVA (2026-08-12) ───────────────────────────────────
  // Itt SZÁNDÉKOSAN NINCS `posthog.group()`. Korábban a cég adószáma jött
  // build-time env-változóból (`REACT_APP_PUBLIC_POSTHOG_COMPANY_ID`) — az viszont
  // GLOBÁLIS, cégfüggetlen érték, tehát multi-tenantban minden ügyfél
  // bejelentkezés előtti eseménye PhenoGyde csoportjába esett volna.
  //
  // A valódi csoportosítást a bejelentkezés után az `AuthContext`
  // (`syncAnalyticsIdentity`) állítja be a `company_id`-ból. Bejelentkezés ELŐTT
  // nem tudjuk, melyik cég az — és egy HIÁNYZÓ címke jobb, mint egy HAMIS
  // (ugyanez az elv a backendben: `posthog_analytics.capture_for`).

  // A diagnosztikai gyűjtő a hibajelentő widgethez.
  installDiagnostics();

  // A widget innen éri el a session replay linket.
  window.posthog = posthog;
} else {
  // PostHog nélkül is kellenek a konzolhibák a hibajelentő widgethez.
  installDiagnostics();
}

if (!POSTHOG_KEY && process.env.NODE_ENV !== 'production') {
  console.error(
    'REACT_APP_PUBLIC_POSTHOG_KEY variable required by PostHog is missing or un-configured, ' +
    'this causes events to be silently missed. This error stops appearing once REACT_APP_PUBLIC_POSTHOG_KEY is configured'
  );
}

const qc = new QueryClient({ defaultOptions: { queries: { retry: 1, staleTime: 30000 } } });

ReactDOM.createRoot(document.getElementById('root')).render(
  <PostHogProvider client={posthog}>
    <PostHogErrorBoundary>
      <QueryClientProvider client={qc}><App /></QueryClientProvider>
    </PostHogErrorBoundary>
  </PostHogProvider>
);
